import { Link } from "@tanstack/react-router";
import { ArrowRight, ShoppingBag, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BuilderWidget, RichProduct } from "@/lib/products";

interface Ctx {
  liveProducts: RichProduct[];
}

/**
 * Renders a single builder widget. Pure, used both inside the live shop and
 * inside the staff builder canvas (with a thin selection overlay added by the
 * canvas itself).
 */
export function WidgetRenderer({ widget, ctx }: { widget: BuilderWidget; ctx: Ctx }) {
  const p = widget.props ?? {};
  switch (widget.type) {
    case "hero":
      return (
        <div
          className={cn(
            "relative overflow-hidden rounded-[var(--hero-radius,32px)] bg-gradient-hero text-primary-foreground",
            "px-6 py-10 sm:px-10 sm:py-16",
          )}
          style={
            {
              ["--hero-radius" as string]: `${p.radius ?? 32}px`,
              minHeight: p.height ? `${p.height}px` : undefined,
              backgroundImage: p.imageUrl
                ? `linear-gradient(rgba(0,0,0,.45),rgba(0,0,0,.45)), url(${p.imageUrl})`
                : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            } as React.CSSProperties
          }
        >
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">
            {p.title ?? "Your headline here"}
          </h1>
          {p.subtitle && (
            <p className="mt-3 max-w-2xl text-base sm:text-lg opacity-90">{p.subtitle}</p>
          )}
          {p.ctaText && (
            <Link to={p.ctaHref ?? "/app/customer/shop"} className="mt-6 inline-flex">
              <button className="rounded-full bg-white/95 px-5 py-2.5 text-sm font-semibold text-foreground shadow-soft hover:bg-white">
                {p.ctaText} <ArrowRight className="ml-1 inline h-4 w-4" />
              </button>
            </Link>
          )}
        </div>
      );

    case "featured": {
      const ids: string[] = Array.isArray(p.productIds) ? p.productIds : [];
      const items = ctx.liveProducts.filter((x) => ids.includes(x.id));
      const layout = (p.layout ?? "grid") as "grid" | "bento" | "carousel";
      if (items.length === 0) {
        return (
          <EmptyHint
            icon={<Star className="h-4 w-4" />}
            label="Featured products — pick products in the right panel"
          />
        );
      }
      return (
        <div
          className={cn(
            layout === "carousel" ? "flex gap-4 overflow-x-auto pb-2 snap-x" : "grid gap-4",
            layout === "grid" && "grid-cols-2 sm:grid-cols-3 md:grid-cols-4",
            layout === "bento" && "grid-cols-2 sm:grid-cols-4 auto-rows-[160px]",
          )}
        >
          {items.map((prod, i) => (
            <ProductCard
              key={prod.id}
              product={prod}
              className={cn(
                layout === "carousel" && "w-56 shrink-0 snap-start",
                layout === "bento" && (i % 5 === 0 ? "col-span-2 row-span-2" : ""),
              )}
            />
          ))}
        </div>
      );
    }

    case "product_grid": {
      const items = ctx.liveProducts.slice(0, p.limit ?? 12);
      if (items.length === 0) {
        return (
          <EmptyHint icon={<ShoppingBag className="h-4 w-4" />} label="No live products yet" />
        );
      }
      return (
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
          {items.map((prod) => (
            <ProductCard key={prod.id} product={prod} />
          ))}
        </div>
      );
    }

    case "heading": {
      const level = Math.min(Math.max(Number(p.level ?? 2), 1), 4);
      const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4";
      return (
        <Tag
          className={cn(
            "font-bold tracking-tight",
            p.align === "center" && "text-center",
            p.align === "right" && "text-right",
            level === 1 && "text-4xl sm:text-5xl",
            level === 2 && "text-2xl sm:text-3xl",
            level === 3 && "text-xl sm:text-2xl",
            level === 4 && "text-lg",
          )}
        >
          {p.text ?? "Heading"}
        </Tag>
      );
    }

    case "text":
      return (
        <p
          className={cn(
            "text-foreground/90 leading-relaxed",
            p.align === "center" && "text-center",
            p.align === "right" && "text-right",
          )}
        >
          {p.text ?? "Add some descriptive copy for your shoppers here."}
        </p>
      );

    case "image":
      return p.url ? (
        <img
          src={p.url}
          alt={p.alt ?? ""}
          className={cn("w-full h-auto object-cover", p.rounded ? "rounded-2xl" : "rounded-none")}
          style={{ maxHeight: p.maxHeight ? `${p.maxHeight}px` : undefined }}
        />
      ) : (
        <EmptyHint label="Image — choose a file in the right panel" />
      );

    case "spacer":
      return <div style={{ height: `${p.height ?? 32}px` }} />;

    case "cta":
      return (
        <div
          className="rounded-3xl bg-gradient-hero px-6 py-10 text-center text-primary-foreground"
          style={p.background ? { background: p.background } : undefined}
        >
          <h3 className="text-2xl font-bold">{p.title ?? "Ready to print?"}</h3>
          {p.subtitle && <p className="mt-2 opacity-90">{p.subtitle}</p>}
          {p.buttonText && (
            <Link to={p.buttonHref ?? "/app/customer/shop"} className="mt-5 inline-flex">
              <button className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-foreground shadow-soft">
                {p.buttonText}
              </button>
            </Link>
          )}
        </div>
      );

    default:
      return null;
  }
}

function ProductCard({ product, className }: { product: RichProduct; className?: string }) {
  return (
    <Link
      to="/app/customer/shop/$productId"
      params={{ productId: product.id }}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-border bg-card transition-all hover:shadow-soft",
        className,
      )}
    >
      <div className="aspect-square overflow-hidden bg-muted">
        {product.image_main_url ? (
          <img
            src={product.image_main_url}
            alt={product.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-8 w-8" />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="truncate text-sm font-semibold">{product.name}</div>
        {product.short_description && (
          <div className="truncate text-xs text-muted-foreground">{product.short_description}</div>
        )}
      </div>
    </Link>
  );
}

function EmptyHint({ icon, label }: { icon?: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-muted/30 py-8 text-xs text-muted-foreground">
      {icon}
      <span>{label}</span>
    </div>
  );
}
